import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import useAuthStore from '../store/authStore';
import { getAllPosters, getPosterById, createPoster, updatePoster, deletePoster } from '../api/postersApi';

export const usePosters = () => {
  const { setError } = useAuthStore();

  const {
    data: posters,
    isLoading,
    error,
    refetch
  } = useQuery('posters', getAllPosters, {
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to fetch posters';
      setError(errorMessage);
      toast.error(errorMessage);
    },
    retry: false,
    staleTime: 300000, // Consider data fresh for 5 minutes
    cacheTime: 3600000, // Keep data in cache for 1 hour
  });

  return {
    posters: posters?.data || [],
    isLoading,
    error,
    refetchPosters: refetch
  };
};

export const usePoster = (posterId) => {
  const { setError } = useAuthStore();

  const {
    data: poster,
    isLoading,
    error,
    refetch
  } = useQuery(
    ['poster', posterId],
    () => getPosterById(posterId),
    {
      onError: (error) => {
        console.error('Query error:', error);
        const errorMessage = error?.response?.data?.message || 'Failed to fetch poster details';
        setError(errorMessage);
        toast.error(errorMessage);
      },
      retry: 1,
      staleTime: 300000,
      cacheTime: 3600000,
      enabled: posterId !== undefined && posterId !== null,
    }
  );

  return {
    poster: poster?.data,
    isLoading,
    error,
    refetchPoster: refetch
  };
};

export const useCreatePoster = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(createPoster, {
    onSuccess: (data) => {
      toast.success('Poster created successfully!');
      queryClient.invalidateQueries('posters');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create poster';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useUpdatePoster = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(({ id, formData }) => updatePoster(id, formData), {
    onSuccess: (data, variables) => {
      toast.success('Poster updated successfully!');
      queryClient.invalidateQueries('posters');
      queryClient.invalidateQueries(['poster', variables.id]);
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update poster';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
};

export const useDeletePoster = () => {
  const queryClient = useQueryClient();
  const { setError } = useAuthStore();

  return useMutation(deletePoster, {
    onSuccess: (data) => {
      toast.success('Poster deleted successfully!');
      queryClient.invalidateQueries('posters');
    },
    onError: (error) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete poster';
      setError(errorMessage);
      toast.error(errorMessage);
    },
  });
}; 